import React from "react";
import { DatePicker, Input, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";

import {
  EMPLOYEE_STATUS_OPTIONS,
  DEFAULT_EMPLOYEE_STATUS,
  STATUS_OPTIONS,
} from "../constants/attendanceAdmin.constants";

import type {
  AttendanceStatusFilter,
  DepartmentOption,
  EmployeeStatusFilter,
} from "../types";

interface AttendanceAdminFiltersProps {
  status: AttendanceStatusFilter;
  empStatus: EmployeeStatusFilter;
  departmentValue: number | string | null;
  date: string;
  search: string;
  departments: DepartmentOption[];
  onSearchChange: (value: string) => void;
  onStatusChange: (status: AttendanceStatusFilter) => void;
  onEmpStatusChange: (empStatus: EmployeeStatusFilter) => void;
  onDepartmentChange: (department: DepartmentOption | null) => void;
  onDateChange: (date: string) => void;
  onReset: () => void;
}

const filterOption = (input: string, option?: { label: string; value: any }) =>
  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase());

const AttendanceAdminFilters: React.FC<AttendanceAdminFiltersProps> = ({
  status,
  empStatus,
  departmentValue,
  date,
  search,
  departments,
  onSearchChange,
  onStatusChange,
  onEmpStatusChange,
  onDepartmentChange,
  onDateChange,
  onReset,
}) => {
  const activeFiltersCount =
    (status !== "All" ? 1 : 0) +
    (empStatus !== DEFAULT_EMPLOYEE_STATUS ? 1 : 0) +
    (departmentValue ? 1 : 0) +
    (date !== dayjs().format("YYYY-MM-DD") ? 1 : 0) +
    (search.trim() ? 1 : 0);

  return (
    <div className="card">
      <div className="card-header border-0 pb-0">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <i className="ti ti-clock-check text-info me-2" style={{ fontSize: 20 }} />
              Attendance Records
            </h4>
            <p className="text-muted mb-0">
              View daily attendance — filter by department, status, or date
            </p>
          </div>
        </div>
      </div>

      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2">
        <div className="d-flex gap-2 flex-wrap">
          <div className="me-2 mb-2">
            <Input
              placeholder="Search by employee name..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              prefix={<i className="ti ti-search" style={{ color: "#94a3b8" }} />}
              style={{ width: 260, minHeight: 38 }}
              allowClear
            />
          </div>
          <div className="me-2 mb-2">
            <DatePicker
              value={date ? dayjs(date) : null}
              onChange={(d: Dayjs | null) =>
                onDateChange(d ? d.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"))
              }
              format="DD MMM YYYY"
              style={{ width: 180, minHeight: 38 }}
              allowClear={false}
            />
          </div>
        </div>

        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-2">
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 220, minHeight: 38 }}
              placeholder="Department"
              value={departmentValue ?? undefined}
              allowClear
              onChange={(value) => {
                if (!value) {
                  onDepartmentChange(null);
                  return;
                }
                const match = departments.find((d) => d.value === value);
                onDepartmentChange(match || null);
              }}
              options={departments.map((d) => ({ value: d.value, label: d.label }))}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              style={{ width: 160, minHeight: 38 }}
              placeholder="Status"
              value={status}
              onChange={(value) => onStatusChange(value as AttendanceStatusFilter)}
              options={STATUS_OPTIONS}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              style={{ width: 180, minHeight: 38 }}
              placeholder="Employee Status"
              value={empStatus}
              onChange={(value) => onEmpStatusChange(value as EmployeeStatusFilter)}
              options={EMPLOYEE_STATUS_OPTIONS}
            />
          </div>
          <div>
            <button
              type="button"
              className={`btn btn-md ${activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"} mb-2 position-relative py-2`}
              onClick={onReset}
              disabled={activeFiltersCount === 0}
            >
              <i className="ti ti-refresh me-1" />
              Reset
              {activeFiltersCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "10px" }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceAdminFilters;
